import * as THREE from 'three';
import { mulberry32, vehicleById } from '@typing-racer/shared';
import { blockyStyle } from './styles/blocky.js';
import { modelStyle } from './styles/model.js';
import { getStyle, type StyleId, type StyleRenderer } from './style.js';
import { ROAD_WIDTH, Track } from './track.js';
import { VehicleActor } from './vehicle.js';
import { buildBillboards, type BoardInfo } from './billboards.js';
import type { AffiliatePayload } from '../screens/affiliate.js';

export type CameraMode = 'preview' | 'chase' | 'side' | 'pan' | 'overview';
export type ViewMode = 'chase' | 'side' | 'pan';
export const VIEW_MODES: { id: ViewMode; name: string; icon: string }[] = [
  { id: 'chase', name: 'Behind', icon: '🎥' },
  { id: 'side', name: 'Side', icon: '📷' },
  { id: 'pan', name: 'Panning', icon: '🎬' },
];
const VIEW_KEY = 'typing-racer.view';
export function loadView(): ViewMode {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    if (v === 'side' || v === 'pan' || v === 'chase') return v;
  } catch {
    /* ignore */
  }
  return 'chase';
}

export class GameScene {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly track = new Track();
  private style: StyleRenderer;
  private world = new THREE.Group();
  private vehicles = new Map<string, VehicleActor>();
  private preview: VehicleActor | null = null;
  private localId: string | null = null;
  mode: CameraMode = 'preview';
  /** The gameplay camera the player picked; `mode` switches to it when racing. */
  view: ViewMode = loadView();
  reduceMotion = false;
  private panAngle = 0;
  private clock = new THREE.Clock();
  private camPos = new THREE.Vector3();
  private camLook = new THREE.Vector3();
  private baseFov = 60;
  private buildToken = 0;
  private sun: THREE.DirectionalLight;
  onFrame: ((dt: number) => void) | null = null;
  private ads: AffiliatePayload | null = null;
  private boards = new THREE.Group();
  private boardPanels: THREE.Mesh[] = [];
  private raycaster = new THREE.Raycaster();
  /** Called when a trackside board is clicked. */
  onBoardClick: ((board: BoardInfo) => void) | null = null;
  /** Lateral offset of the preview car from screen centre (world units). */
  previewSide = 4.5;
  /** How far ahead of the car the chase camera looks; 0 centres the car (used in lessons). */
  lookAhead = 5;

  constructor(container: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(this.renderer.domElement);

    this.scene.background = new THREE.Color('#8fd3ff');
    this.scene.fog = new THREE.Fog('#8fd3ff', 140, 420);
    this.camera = new THREE.PerspectiveCamera(this.baseFov, 1, 0.1, 800);

    const hemi = new THREE.HemisphereLight('#bfe6ff', '#5ab84a', 0.9);
    this.scene.add(hemi);
    this.sun = new THREE.DirectionalLight('#ffffff', 2.2);
    this.sun.position.set(60, 90, 40);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    const s = 80;
    Object.assign(this.sun.shadow.camera, { left: -s, right: s, top: s, bottom: -s, near: 1, far: 300 });
    this.sun.shadow.bias = -0.0005;
    this.scene.add(this.sun, this.sun.target);
    this.scene.add(this.world);

    this.style = getStyle() === 'model' ? modelStyle : blockyStyle;
    void this.buildWorld();

    this.resize();
    addEventListener('resize', () => this.resize());
    new ResizeObserver(() => this.resize()).observe(container);
    this.scene.add(this.boards);
    const canvas = this.renderer.domElement;
    canvas.addEventListener('click', (e) => {
      const b = this.pickBoard(e.clientX, e.clientY);
      if (b) this.onBoardClick?.(b);
    });
    let hoverTick = 0;
    canvas.addEventListener('pointermove', (e) => {
      if (++hoverTick % 4) return; // raycast at a quarter of the pointer rate
      canvas.style.cursor = this.pickBoard(e.clientX, e.clientY) ? 'pointer' : '';
    });
    this.renderer.setAnimationLoop(() => this.frame());
  }

  /** Provide the affiliate payload; boards are (re)built along the track. */
  setAds(ads: AffiliatePayload): void {
    this.ads = ads;
    this.scene.remove(this.boards);
    const built = buildBillboards(this.track, ads);
    this.boards = built.group;
    this.boardPanels = built.panels;
    this.scene.add(this.boards);
  }

  private pickBoard(clientX: number, clientY: number): BoardInfo | null {
    if (!this.boardPanels.length) return null;
    const rect = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(ndc, this.camera);
    const hit = this.raycaster.intersectObjects(this.boardPanels, false)[0];
    return hit ? ((hit.object.userData as { board: BoardInfo }).board ?? null) : null;
  }

  get styleId(): StyleId {
    return this.style.id;
  }

  async setStyle(id: StyleId): Promise<void> {
    if (id === this.style.id) return;
    this.style = id === 'model' ? modelStyle : blockyStyle;
    await this.buildWorld();
    // Rebuild vehicles in the new style.
    for (const v of this.vehicles.values()) {
      const meta = v.root.userData as { vehicle: string; colour: string };
      v.setBuilt(await this.style.buildVehicle(vehicleById(meta.vehicle), meta.colour));
    }
    if (this.preview) {
      const meta = this.preview.root.userData as { vehicle: string; colour: string };
      this.preview.setBuilt(await this.style.buildVehicle(vehicleById(meta.vehicle), meta.colour));
    }
  }

  private async buildWorld(): Promise<void> {
    const token = ++this.buildToken;
    const world = new THREE.Group();
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(1200, 1200), this.style.groundMaterial());
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    world.add(ground);
    world.add(this.track.buildMesh(this.style));

    // Scenery along the outside and inside of the track.
    const rng = mulberry32(1234);
    const up = new THREE.Vector3(0, 1, 0);
    const trees: Promise<void>[] = [];
    for (let i = 0; i < 90; i++) {
      const u = rng();
      const p = this.track.curve.getPointAt(u);
      const t = this.track.curve.getTangentAt(u);
      const n = new THREE.Vector3().crossVectors(up, t);
      const side = rng() > 0.5 ? 1 : -1;
      const off = ROAD_WIDTH / 2 + 10 + rng() * 25;
      if (u < 0.03 || u > 0.97) continue; // keep the grandstand clear
      trees.push(
        this.style.buildTree(rng).then((tree) => {
          tree.position.copy(p).addScaledVector(n, side * off);
          world.add(tree);
        }),
      );
    }
    for (let i = 0; i < 14; i++) {
      const u = 0.08 + (i / 14) * 0.86;
      const p = this.track.curve.getPointAt(u);
      const t = this.track.curve.getTangentAt(u);
      const n = new THREE.Vector3().crossVectors(up, t);
      const side = i % 3 === 0 ? -1 : 1;
      trees.push(
        this.style.buildBuilding(rng).then((b) => {
          b.position.copy(p).addScaledVector(n, side * (ROAD_WIDTH / 2 + 22 + rng() * 15));
          b.rotation.y = Math.atan2(t.x, t.z);
          world.add(b);
        }),
      );
    }
    await Promise.all(trees);
    if (token !== this.buildToken) return;
    this.scene.remove(this.world);
    this.world = world;
    this.scene.add(world);
  }

  // ------------------------------------------------------------ vehicles
  async addVehicle(id: string, vehicleId: string, colour: string, lane: number): Promise<VehicleActor> {
    let actor = this.vehicles.get(id);
    if (!actor) {
      actor = new VehicleActor(id, lane);
      this.vehicles.set(id, actor);
      this.scene.add(actor.root);
      actor.update(0, this.track);
    }
    actor.lane = lane;
    const meta = actor.root.userData as { vehicle?: string; colour?: string };
    if (meta.vehicle !== vehicleId || meta.colour !== colour) {
      actor.root.userData = { vehicle: vehicleId, colour };
      actor.setBuilt(await this.style.buildVehicle(vehicleById(vehicleId), colour));
    }
    return actor;
  }

  removeVehicle(id: string): void {
    const v = this.vehicles.get(id);
    if (!v) return;
    this.scene.remove(v.root);
    this.vehicles.delete(id);
  }

  clearVehicles(): void {
    for (const id of this.vehicleIds()) this.removeVehicle(id);
  }

  vehicleIds(): string[] {
    return [...this.vehicles.keys()];
  }

  getVehicle(id: string): VehicleActor | undefined {
    return this.vehicles.get(id);
  }

  setLocal(id: string | null): void {
    this.localId = id;
  }

  /** Switch to the gameplay camera (chase/side/pan). */
  race(): void {
    this.mode = this.view;
  }

  setView(v: ViewMode): void {
    this.view = v;
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* ignore */
    }
    if (this.mode === 'chase' || this.mode === 'side' || this.mode === 'pan') this.mode = v;
  }

  cycleView(): ViewMode {
    const i = VIEW_MODES.findIndex((m) => m.id === this.view);
    const next = VIEW_MODES[(i + 1) % VIEW_MODES.length]!.id;
    this.setView(next);
    return next;
  }

  resetProgress(): void {
    for (const v of this.vehicles.values()) v.snap(0);
  }

  async setPreview(vehicleId: string, colour: string): Promise<void> {
    if (!this.preview) {
      this.preview = new VehicleActor('preview', 2.5);
      this.preview.snap(0.004);
      this.preview.update(0, this.track);
    }
    const meta = this.preview.root.userData as { vehicle?: string; colour?: string };
    if (meta.vehicle === vehicleId && meta.colour === colour && this.preview.root.parent) return;
    this.preview.root.userData = { vehicle: vehicleId, colour };
    this.preview.setBuilt(await this.style.buildVehicle(vehicleById(vehicleId), colour));
    if (!this.preview.root.parent) this.scene.add(this.preview.root);
  }

  hidePreview(): void {
    if (this.preview?.root.parent) this.scene.remove(this.preview.root);
  }

  // ------------------------------------------------------------- loop
  private resize(): void {
    const w = this.renderer.domElement.parentElement?.clientWidth || innerWidth;
    const h = this.renderer.domElement.parentElement?.clientHeight || innerHeight;
    if (w === 0 || h === 0) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  private frame(): void {
    const dt = Math.min(0.1, this.clock.getDelta());
    for (const v of this.vehicles.values()) v.update(dt, this.track);
    this.updateCamera(dt);
    this.onFrame?.(dt);
    this.renderer.render(this.scene, this.camera);
  }

  private updateCamera(dt: number): void {
    const k = 1 - Math.exp(-dt * 8);
    const t = performance.now() / 1000;
    if (this.mode === 'preview' && this.preview) {
      const c = this.preview.root.position;
      const a = t * 0.5;
      this.camPos.set(c.x + Math.sin(a) * 9, 4.5, c.z + Math.cos(a) * 9);
      // Look slightly to the camera's left so the car sits right of centre, clear of the lobby panels.
      const rx = Math.cos(a);
      const rz = -Math.sin(a);
      const side = this.previewSide;
      this.camLook.set(c.x - rx * side, 1.2, c.z - rz * side);
      this.camera.position.lerp(this.camPos, k);
      this.camera.fov += (50 - this.camera.fov) * k;
    } else if (this.mode === 'chase' || this.mode === 'side' || this.mode === 'pan') {
      const v = (this.localId && this.vehicles.get(this.localId)) || [...this.vehicles.values()][0];
      if (!v) return;
      const fwd = new THREE.Vector3(0, 0, 1).applyQuaternion(v.root.quaternion);
      const right = new THREE.Vector3().crossVectors(fwd, new THREE.Vector3(0, 1, 0));
      const speed01 = Math.min(1, Math.abs(v.speed) / 25);
      let fov = this.baseFov;
      if (this.mode === 'chase') {
        this.camPos.copy(v.root.position).addScaledVector(fwd, -13).add(new THREE.Vector3(0, 6.5 + (5 - this.lookAhead) * 0.4, 0));
        this.camLook.copy(v.root.position).addScaledVector(fwd, this.lookAhead).add(new THREE.Vector3(0, 2.5 - (5 - this.lookAhead) * 0.3, 0));
        if (!this.reduceMotion) fov += speed01 * 18;
      } else if (this.mode === 'side') {
        // Track-side camera keeping pace with the car, slightly ahead so the road is visible.
        // Stay within ~9 units of the car so the camera never enters roadside scenery.
        this.camPos.copy(v.root.position).addScaledVector(right, 9).addScaledVector(fwd, 1.5).add(new THREE.Vector3(0, 4.2, 0));
        this.camLook.copy(v.root.position).addScaledVector(fwd, 1).add(new THREE.Vector3(0, 1.2, 0));
        fov = 55;
      } else {
        // Slow cinematic orbit around the car.
        this.panAngle += dt * (this.reduceMotion ? 0.08 : 0.18);
        const r = 16;
        this.camPos.set(
          v.root.position.x + Math.sin(this.panAngle) * r,
          7 + Math.sin(this.panAngle * 0.7) * 2,
          v.root.position.z + Math.cos(this.panAngle) * r,
        );
        this.camLook.copy(v.root.position).add(new THREE.Vector3(0, 1.5, 0));
        fov = 48;
      }
      this.camera.position.lerp(this.camPos, this.mode === 'pan' ? Math.min(1, dt * 3) : k);
      this.camera.fov += (fov - this.camera.fov) * k;
    } else {
      // overview: slow orbit over the start line
      const p0 = this.track.curve.getPointAt(0.02);
      this.camPos.set(p0.x + Math.sin(t * 0.2) * 30, 18, p0.z + Math.cos(t * 0.2) * 30);
      this.camLook.set(p0.x, 0, p0.z);
      this.camera.position.lerp(this.camPos, k);
      this.camera.fov += (55 - this.camera.fov) * k;
    }
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(this.camLook);
    // Keep the shadow frustum near the camera target.
    this.sun.position.copy(this.camLook).add(new THREE.Vector3(60, 90, 40));
    this.sun.target.position.copy(this.camLook);
  }
}
